<template>
  <div class="w-full">
    <div class="mt-5">
      <form @submit="onSubmit">
        <div class="flex flex-col items-center">
          <div class="mb-8 flex flex-col gap-3 text-center">
            <h3 class="text-lg font-semibold text-white">{{ t("signup.verifyEmail") }}</h3>
            <p class="text-colorGrey4">{{ t("signup.SentAnEmail") }} {{ props.email }}</p>
            <p class="text-colorGrey4">
              {{ t("signup.pleaseEnterTheDigitalCode") }}
            </p>
          </div>

          <div class="w-[320px] space-y-6 mx-auto">
            <FormField v-slot="{ componentField, value }" name="pin">
              <FormItem>
                <FormControl>
                  <PinInput
                    id="pin-input"
                    :model-value="value"
                    placeholder=""
                    class="flex gap-2 items-center mt-1 justify-center"
                    otp
                    type="text"
                    :name="componentField.name"
                    @complete="handleComplete"
                    @update:model-value="
                      (arrStr: string[]) => {
                        // 清除之前的錯誤狀態
                        if (errors.pin) {
                          resetField('pin', { value: arrStr });
                        }
                        
                        if (arrStr.filter(Boolean).length < 6) {
                          verifyEnable = false;
                        }
                        for(let i=0; i<arrStr.length; i++) {
                          if(!!!arrStr[i]) {
                            arrStr[i] = '';
                          }
                        }
                        setFieldValue('pin', arrStr);
                      }
                    "
                  >
                    <PinInputGroup class="gap-4">
                      <PinInputInput
                        v-for="(id, index) in 6"
                        :key="id"
                        :index="index"
                        class="bg-background border rounded-md h-14 w-10 text-lg text-white focus:border-gray-50"
                        :class="{ 
                          'border-gray-50': !errors.pin,
                          'border-red-500': errors.pin 
                        }"
                      />
                    </PinInputGroup>
                  </PinInput>
                </FormControl>
                <div class="flex justify-center">
                  <FormMessage class="text-base" />
                </div>
              </FormItem>
            </FormField>
          </div>
          
          <div class="my-5">
            <p v-if="countDownFormat !== '' && showResend" class="text-colorGrey4 text-center">
              {{ countDownFormat }}
              <span class="">{{
                t("signup.toResend")
              }}</span>
            </p>
            <p
              v-else-if="showResend && countDown === -1 && countDownFormat === '' "
              class="cursor-pointer text-lightPurple text-center"
              @click="resend"
            >
              {{ t("signup.ResendCode") }}
            </p>
          </div>
          
          <div class="flex gap-3 w-full">
            <Button
              type="button"
              variant="outline"
              class="flex-1"
              @click="handleCancel"
            >
              {{ t("Cancel") }}
            </Button>
            <Button :disabled="!verifyEnable" type="submit" class="flex-1">
              {{ t("Verify") }}
            </Button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  PinInput,
  PinInputGroup,
  PinInputInput,
} from "@/components/ui/pin-input";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import Button from "@/components/ui/button/Button.vue";
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import * as z from "zod";

import { useI18n } from "vue-i18n";

import { ref } from "vue"

const config = useRuntimeConfig();

const emailVerify = defineModel<boolean>('emailVerify', {required: true});

const emit = defineEmits<{
  startVerify: []
  cancel: []
  verified: [token: string]
}>()

;

const { t } = useI18n();

interface APIError {
  data: {
    result: boolean;
    code: number; 
    message: string;
  }
}

const props = withDefaults(
  defineProps<{
    email?: string;
    disable?: boolean;
    emailError?: string;
  }>(),
  {
    email: "",
    disable: true,
    emailError: "",
  }
);

onMounted(() => {
  setFieldValue('pin', []);
  verifyEnable.value = false;
  
  // 啟動倒數計時
  countDownTimer(300, timer);
  showResend.value = true;
  
  // 直接處理 emailError
  if (props.emailError) {
    setFieldError('pin', props.emailError);
  }
});


const emailToken = defineModel<string>('emailToken', {required: true});


const formSchema = toTypedSchema(
  z.object({
    pin: z.array(z.string()),
  })
);

const { handleSubmit, setFieldValue, setFieldError, resetField, errors } = useForm({
  validationSchema: formSchema,
  initialValues: {
    pin: [],
  },
});
const onSubmit = handleSubmit( async(pin) => {
  const pinCode = pin.pin.join('');
  
  // 客戶端驗證
  if (pinCode.length !== 6 || pin.pin.some(digit => !digit)) {
    setFieldError('pin', 'Please enter a valid code');
    return;
  }
  
  try {
    const result = await $fetch<{result: boolean, emailToken: string}>( `${config.public.APIurl}/api/v3/register/email/verify`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: { 
        email: props.email,
        emailVerifyCode: pinCode,
        btnResend: false
      }
    })
    emailToken.value = result.emailToken
    emailVerify.value = true;
    emit('verified', result.emailToken);
  }catch(error : unknown) {
    const err = error as APIError;
    if(err.data.code === 17006) {
      setFieldError('pin', t('17007'))
    }else {
      setFieldError('pin', 'Please enter a valid code')
    }
    console.error(err.data.message)
  }
});

const showResend = ref(false);
const resend = async () => {
  const success = await sendVerifyEmail();
  if (success) {
    countDownTimer(300, timer);
  }
  // 錯誤訊息會顯示在 pin field 下方
};

const handleComplete = () => (verifyEnable.value = true);

const countDown = ref<number>(0);
const countDownFormat = ref<string>("");
let timer = ref<any>(null);


const handleCancel = () => {
  clearCounter();
  clearInterval(timer.value);
  showResend.value = false;
  resetField('pin');
  emit('cancel');
};

const sendVerifyEmail = async (): Promise<boolean> => {
  try {
    await $fetch(`${config.public.APIurl}/api/v3/register/email/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: { 
        email: props.email, 
      }
    });
    return true;
  } catch(error: unknown) {
    const err = error as APIError;
    console.error(err.data.message);
    if(err.data.code === 17006) {
      setFieldError('pin', t('17006'));
    } else if(err.data.code === 12006) {
      setFieldError('pin', err.data.message);
    } else {
      setFieldError('pin', 'something went wrong');
    }
    return false;
  }
}

const countDownTimer = (sec: number, timer: Ref<any>) => {
  // 清除現有的 timer，避免重複建立 interval
  if (timer.value) {
    clearInterval(timer.value);
  }
  
  countDown.value = sec;
  let countDownSec = 0;
  let countDownMin = 0;

  timer.value = setInterval(() => {
    countDownSec = countDown.value % 60;
    countDownMin = Math.floor(countDown.value / 60);
    countDownFormat.value =
      String(countDownMin).padStart(2, "0") +
      ":" +
      String(countDownSec).padStart(2, "0");
    if (countDown.value <= 0) {
      clearInterval(timer.value);
      clearCounter();
      return;
    }
    countDown.value--;
    //
  }, 1000);
};

const verifyEnable = ref(false);

const clearCounter = () => {
  countDown.value = -1;
  countDownFormat.value = "";
};
</script>
